import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import logoIcon from '../../assets/images/standalone_logo.png';
import logoFull from '../../assets/images/logo_aktivity.png';
import participantService from '../../services/participantService';
import tokenStorage from '../../utils/tokenStorage';
import useWakeLock from '../../hooks/useWakeLock';

/**
 * Session Welcome Page
 * Shown when a participant first joins a session
 * Displays welcome message, registration confirmation, and session details
 */

// Format session date and time for display
const formatSessionDateTime = (startTime, endTime) => {
  if (!startTime) return null;

  const start = new Date(startTime);
  const end = endTime ? new Date(endTime) : null;

  const dateOptions = { day: 'numeric', month: 'short', year: 'numeric' };
  const timeOptions = { hour: 'numeric', minute: '2-digit', hour12: true };

  const startDate = start.toLocaleDateString('en-US', dateOptions);
  const startTimeStr = start.toLocaleTimeString('en-US', timeOptions);

  if (!end) {
    return { date: startDate, time: startTimeStr, isMultiDay: false };
  }

  const endTimeStr = end.toLocaleTimeString('en-US', timeOptions);
  const isSameDay = start.toDateString() === end.toDateString();

  if (isSameDay) {
    return { date: startDate, time: `${startTimeStr} - ${endTimeStr}`, isMultiDay: false };
  } else {
    const endDate = end.toLocaleDateString('en-US', dateOptions);
    return { date: `${startDate} - ${endDate}`, time: `${startTimeStr} - ${endTimeStr}`, isMultiDay: true };
  }
};

const SessionWelcomePage = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  // Fix #9: keep screen awake on welcome page during an active session
  useWakeLock(true);
  const [session, setSession] = useState(null);
  const [participant, setParticipant] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSessionData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch session from API
        const response = await participantService.getSession(sessionId);
        if (response.success && response.data) {
          setSession(response.data);
        } else {
          setError('Session not found');
        }
      } catch (err) {
        console.error('Error fetching session:', err);
        setError(err.message || 'Failed to load session');
      } finally {
        setLoading(false);
      }
    };

    fetchSessionData();

    // Get participant info from dual storage (auto-restores from sessionStorage if purged)
    const storedParticipant = tokenStorage.getParticipantData(sessionId);
    if (storedParticipant) {
      setParticipant(storedParticipant);
    }
  }, [sessionId]);

  const handleContinue = () => {
    navigate(`/session/${sessionId}/lobby`);
  };

  // Navigation items for sidebar
  const navigationItems = [
    {
      id: 'dashboard',
      name: 'Dashboard',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/lobby`),
    },
    {
      id: 'workbooks',
      name: 'Workbooks',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/launched-workbooks`),
    },
    {
      id: 'activities',
      name: 'Aktivities',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/active`),
      badge: true,
    },
    {
      id: 'reports',
      name: 'Reports',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/reports`),
    },
    {
      id: 'workspace',
      name: 'Workspace',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/notes`),
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-600 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">Session Not Found</h2>
          <p className="text-neutral-600 mb-6">{error || 'The session you are looking for does not exist or has been removed.'}</p>
          <button
            onClick={() => navigate('/join')}
            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
          >
            Join Another Session
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-neutral-200 fixed h-full z-30">
        {/* Logo */}
        <div className="flex items-center gap-2 px-4 py-4 border-b border-neutral-200">
          <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
          <img src={logoFull} alt="Aktivity" className="h-7 object-contain" />
        </div>

        {/* Session Info */}
        <div className="px-4 py-4 border-b border-neutral-200">
          <div className="flex items-center gap-2 mb-2">
            <span className="flex items-center gap-1.5 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
              Live
            </span>
            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded">
              #{session.code}
            </span>
          </div>
          <h3 className="font-semibold text-neutral-900 text-sm truncate">{session.name}</h3>
          <p className="text-xs text-neutral-500 truncate">{session.trainer?.name || 'Trainer'}</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navigationItems.map((item) => (
            <button
              key={item.id}
              onClick={item.onClick}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
            >
              {item.icon}
              <span className="flex-1 text-left">{item.name}</span>
              {item.badge && (
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              )}
            </button>
          ))}
        </nav>

        {/* Participant Info */}
        {participant && (
          <div className="p-4 border-t border-neutral-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-primary-600 font-semibold">
                  {participant.name?.charAt(0)?.toUpperCase() || 'P'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-900 truncate">{participant.name}</p>
                <p className="text-xs text-neutral-500">Participant</p>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-neutral-200">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-2 text-neutral-600 hover:text-neutral-900"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <img src={logoIcon} alt="Logo" className="h-7 w-7 object-contain" />
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-medium rounded-full">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
              Live
            </span>
            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded">
              #{session.code}
            </span>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)}></div>
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white flex flex-col">
            {/* Sidebar Header */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-neutral-200">
              <div className="flex items-center gap-2">
                <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                <img src={logoFull} alt="Aktivity" className="h-7 object-contain" />
              </div>
              <button onClick={() => setSidebarOpen(false)} className="p-2 text-neutral-500 hover:text-neutral-700">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Session Info */}
            <div className="px-4 py-4 border-b border-neutral-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="flex items-center gap-1.5 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                  Live
                </span>
                <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded">
                  #{session.code}
                </span>
              </div>
              <h3 className="font-semibold text-neutral-900 text-sm">{session.name}</h3>
              <p className="text-xs text-neutral-500">{session.trainer?.name || 'Trainer'}</p>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setSidebarOpen(false);
                    item.onClick();
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
                >
                  {item.icon}
                  <span className="flex-1 text-left">{item.name}</span>
                  {item.badge && (
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  )}
                </button>
              ))}
            </nav>

            {/* Participant Info */}
            {participant && (
              <div className="p-4 border-t border-neutral-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-primary-600 font-semibold">
                      {participant.name?.charAt(0)?.toUpperCase() || 'P'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 truncate">{participant.name}</p>
                    <p className="text-xs text-neutral-500">Participant</p>
                  </div>
                </div>
              </div>
            )}
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        <main className="min-h-screen pt-16 lg:pt-0 pb-24 lg:pb-8 px-4 sm:px-6 flex items-center justify-center">
          <div className="w-full max-w-2xl">
            {/* Welcome Card */}
            <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden">
              {/* Success Header */}
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 px-6 py-8 text-center">
                <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Welcome!</h1>
                <p className="text-green-100 text-sm sm:text-base">You have successfully registered for this session</p>
              </div>

              {/* Content */}
              <div className="p-6 sm:p-8">
                {/* Participant Info */}
                {participant && (
                  <div className="flex items-center gap-4 p-4 bg-primary-50 rounded-xl border border-primary-100 mb-6">
                    <div className="w-14 h-14 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl font-bold text-white">
                        {participant.name?.charAt(0)?.toUpperCase() || 'P'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-lg font-semibold text-neutral-900">{participant.name}</p>
                      {participant.email && (
                        <p className="text-sm text-neutral-500 truncate">{participant.email}</p>
                      )}
                      {participant.organization && (
                        <p className="text-sm text-neutral-500">{participant.organization}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Session Info */}
                <div className="space-y-4 mb-8">
                  <h2 className="text-lg font-semibold text-neutral-900">Session Details</h2>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Session Name */}
                    <div className="p-4 bg-neutral-50 rounded-xl">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <span className="text-xs text-neutral-500 uppercase tracking-wide font-medium">Session</span>
                      </div>
                      <p className="font-semibold text-neutral-900">{session.name}</p>
                    </div>

                    {/* Trainer */}
                    <div className="p-4 bg-neutral-50 rounded-xl">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <span className="text-xs text-neutral-500 uppercase tracking-wide font-medium">Trainer</span>
                      </div>
                      <p className="font-semibold text-neutral-900">{session.trainer?.name || 'Trainer'}</p>
                    </div>

                    {/* Session Code */}
                    <div className="p-4 bg-neutral-50 rounded-xl">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                          <span className="text-sm font-bold text-orange-600">#</span>
                        </div>
                        <span className="text-xs text-neutral-500 uppercase tracking-wide font-medium">Session Code</span>
                      </div>
                      <p className="font-bold text-primary-600 text-lg">{session.code}</p>
                    </div>

                    {/* Organization */}
                    <div className="p-4 bg-neutral-50 rounded-xl">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <span className="text-xs text-neutral-500 uppercase tracking-wide font-medium">Organization</span>
                      </div>
                      <p className="font-semibold text-neutral-900">{session.organization?.name || 'N/A'}</p>
                    </div>

                    {/* Date & Time */}
                    {session.startTime && (() => {
                      const dateTime = formatSessionDateTime(session.startTime, session.endTime);
                      if (!dateTime) return null;
                      return (
                        <div className="p-4 bg-neutral-50 rounded-xl sm:col-span-2">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <span className="text-xs text-neutral-500 uppercase tracking-wide font-medium">Date & Time</span>
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                            <p className="font-semibold text-neutral-900">{dateTime.date}</p>
                            <span className="hidden sm:inline text-neutral-400">|</span>
                            <p className="text-sm text-neutral-600">{dateTime.time}{dateTime.isMultiDay ? ' (daily)' : ''}</p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* What's Next */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 mb-8">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-1">What's Next?</h3>
                      <p className="text-sm text-blue-700">
                        Click the button below to go to your dashboard. You can access workbooks, participate in live activities, and track your progress throughout the session.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Continue Button */}
                <button
                  onClick={handleContinue}
                  className="w-full px-6 py-4 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  Continue to Dashboard
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation - Show all items with smaller sizes */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 z-40 safe-area-inset-bottom">
        <nav className="flex justify-around items-center h-14 px-0.5">
          {navigationItems.map((item) => (
            <button
              key={item.id}
              onClick={item.onClick}
              className="flex flex-col items-center justify-center gap-0 px-0.5 py-1 flex-1 min-w-0 transition-colors text-neutral-500"
            >
              <span className="relative [&>svg]:w-4 [&>svg]:h-4">
                {item.icon}
                {item.badge && (
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                )}
              </span>
              <span className="text-[8px] font-medium truncate w-full text-center leading-tight mt-0.5">
                {item.id === 'activities' ? 'Aktivity' : item.id === 'workspace' ? 'Notes' : item.name}
              </span>
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default SessionWelcomePage;
